import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import { has } from '../../../utils.js';
import basic from './common/basic.js';
import mention from './common/mention.js';
import asset from './common/asset.js';
import componentTypes from '../types/components-types.js';
import updater from '../updater.js';

/* State example:
state: {
  byId: {
    "comp_0": {
      "id": "comp_0",
      "name": "Root",
      "type": "container",
      "children": ["comp_1]
    },
    "comp_1": {
      "id": "comp_1",
      "name": "Start folder",
      "type": "container",
      "children": ["comp_2"]
    },
    "comp_2": {
      "id": "comp_2",
      "name": "Death Star",
      "image":'https://i.kinja-img.com/gawker-media/image/upload/s--pjOuSH0V--/c_scale,fl_progressive,q_80,w_800/1879xvpsqz1qojpg.jpg',
      "type": "item",
      "attributes": ["attr_0", "attr_1"],
    }
  }
}
*/

const getterOptions = { mentionType: 'component' };
const actionOptions = {
  type: 'components',
  container: { type: 'components', prop: 'children' },
};

const state = { byId: {} };

const getters = {
  ...basic.getters(getterOptions),
  ...asset.getters(),
  ...mention.getters(getterOptions),

  // Return the url of one component image
  getImageUrl(state, getters, rootState, rootGetters) {
    // The component id - a character to indicate the size: f (full), m (medium), s (small)
    return (id, imageSize) => {
      const component = state.byId[id];
      if (has(component, 'image') && component.image !== null) {
        const { image } = component;
        let imageUrl = '';
        switch (imageSize) {
          case 'f': {
            imageUrl = component.image;
            break;
          }
          case 'm':
          case 's': {
            const indexOfdot = image.lastIndexOf('.');
            imageUrl = `${image.substr(
              0,
              indexOfdot
            )}_${imageSize}${image.substr(indexOfdot)}`;
            break;
          }
          default:
            imageUrl = null; // Invalid case
        }
        const assetsUrl = rootGetters['settings/getAssetsUrl'];
        const project = rootGetters['user/projects/getOpen'];
        return `${assetsUrl}/${project}/${imageUrl}`;
      }
      return null;
    };
  },
};

const actions = {
  ...basic.actions(actionOptions),
  ...asset.actions(actionOptions),

  /**
   * Create a new component as a child of a container.
   * @param   {newItem|newContainer} component      The component type
   * @param   {Object}        props                 Any extra or updated properties
   * @param   {String|null}   tId                   The container id (if null uses the root container)
   * @param   {Number}        tIndex                The index in the container
   * @param   {Boolean}       forceIndex            Whether to force parent reference index
   * @param   {Boolean}       tExpand               Whether to expand parent folder with creation
   * @param   {Boolean}       apply                 Whether to apply the patch after finishing
   * @returns {String}                              The id of the component that was created
   */
  customCreate(
    context,
    {
      component,
      props = {},
      tId = null,
      tIndex = 0,
      forceIndex = false,
      tExpand = false,
      apply = true,
    }
  ) {
    if (tId && tExpand) {
      context.dispatch(
        'project/config/components/setCollapsed',
        { id: tId, value: false, apply: false },
        { root: true }
      );
    }
    // In case no parent container specified use root container
    tId = tId || context.getters.getRootContainer.id;
    const id = uuidv4();
    context.dispatch('create', {
      node: {
        id,
        ...component.data,
        ...props,
      },
      tId,
      tIndex,
      forceIndex,
      apply: false,
    });
    // Create the default configuration for this component (force other users to do this too)
    // if (has(component.data, 'children')) {
    //   context.dispatch(
    //     'project/config/components/customCreate',
    //     { id, component, apply: false, ignore: false },
    //     { root: true }
    //   );
    // }
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Remove a component with specified id from the store
   * @param {String}  id    The id of the component
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Find all the descendants of this component
    const ids = _.cloneDeep(
      context.getters.getDescendantNodes(id, 'components', 'children')
    );

    // Add the current component also
    ids.push(id);

    // Remove (component and descendants) from any component lists in elements
    context.dispatch(
      'project/data/elements/deleteComponents',
      { ids, apply: false },
      { root: true }
    );

    // Remove from any component lists in attributes
    context.dispatch(
      'project/data/attributes/deleteComponents',
      { ids, apply: false },
      { root: true }
    );

    // Remove any mentions in elements, notes, connections, attributes
    ['elements', 'notes', 'connections', 'attributes'].forEach(name => {
      context.dispatch(
        `project/data/${name}/deleteMentions`,
        { ids, type: 'component', apply: false },
        { root: true }
      );
    });

    // Remove all these component attributes
    const attributeIds = [];
    ids.forEach(componentId => {
      const component = context.getters.getNode(componentId);
      if (has(component, 'attributes')) {
        attributeIds.push(...component.attributes);
      }
    });
    context.dispatch(
      'project/data/attributes/deleteMany',
      { ids: attributeIds, apply: false, deleteReference: false },
      { root: true }
    );

    // Deselect all components
    ids.forEach(componentId => {
      context.dispatch(
        'project/config/components/deselect',
        { id: componentId, apply: false },
        { root: true }
      );
    });

    // Delete mentions from clipboard, no patch created
    context.dispatch(
      'application/clipboard/deleteMentions',
      { ids, type: 'component' },
      { root: true }
    );

    // Remove the actual objects (not their references)
    ids
      .filter(componentId => componentId !== id)
      .forEach(componentId => {
        context.dispatch('delete', {
          id: componentId,
          apply: false,
          deleteReference: false,
        });
      });

    // Remove the initial component object and its reference
    context.dispatch('delete', { id, apply: false });

    updater.complete({ apply });
  },
  /**
   * Update some of the component's properties.
   * @param {Object}  context
   * @param {String}  id      The id that should be updated
   * @param {Object}  props   Contains only the properties that are changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    // Update the component's properties
    context.dispatch('update', { id, props, apply: false });
    // Update any mentions in elements, notes, connections, attributes
    if (has(props, 'name')) {
      ['elements', 'notes', 'connections', 'attributes'].forEach(name => {
        context.dispatch(
          `project/data/${name}/updateMentions`,
          {
            id,
            type: 'component',
            name: props.name,
            label: props.name,
            apply: false,
          },
          { root: true }
        );
      });

      // Also update the clipboard (no patch created)
      context.dispatch(
        'application/clipboard/updateMentions',
        { id, type: 'component', name: props.name },
        { root: true }
      );
    }
    updater.complete({ apply });
  },

  /**
   * Create a duplicate of a component at a specified place.
   * @param {Object} context
   * @param {String} id       The id of the component to duplicate
   * @param {String} tId      The id of the container that will host the duplicate
   * @param {Number} tIndex   The index in the array of the container
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  async duplicate(context, { id, tId, tIndex, apply }) {
    // Create the duplicate component with a new id and append 'copy' to its name
    const original = context.getters.getNode(id);

    const isContainer = context.getters.getIsRecursiveContainer(id);
    const component = isContainer
      ? componentTypes.newContainer
      : componentTypes.newItem;

    // Copy the original component properties
    const props = _.cloneDeep(original);
    delete props.id;
    props.name = `${props.name} copy`;
    // Empty any children
    if (has(props, 'children')) {
      props.children = [];
    }
    // Empty any attributes
    if (has(props, 'attributes')) {
      props.attributes = [];
    }

    // Create the component object
    const duplicateId = await context.dispatch('customCreate', {
      component,
      props,
      tId,
      tIndex,
      forceIndex: true,
      apply: false,
    });

    // Create its attributes
    if (has(original, 'attributes')) {
      const attributeIds = [];
      // We will mass create the cloned attributes and add the references later
      const attributeNodes = original.attributes.map(attributeId => {
        const attribute = _.cloneDeep(
          context.rootGetters['project/data/attributes/getNode'](attributeId)
        );
        attribute.id = uuidv4();
        attribute.cId = duplicateId;
        attributeIds.push(attribute.id);
        return attribute;
      });
      await context.dispatch(
        'project/data/attributes/createMany',
        {
          nodes: attributeNodes,
          tId: null,
          tIndex: null,
          apply: false,
        },
        { root: true }
      );
      // Now add the references
      await context.dispatch(
        'project/data/components/createReferences',
        {
          ids: attributeIds,
          rId: duplicateId,
          rType: 'components',
          rProp: 'attributes',
          apply: false,
        },
        { root: true }
      );
    }
    // Create duplicates of children if it is a container
    if (context.getters.getIsRecursiveContainer(id)) {
      /* eslint-disable no-restricted-syntax */
      for (const [index, child] of original.children.entries()) {
        /* eslint-disable no-await-in-loop */
        await context.dispatch('duplicate', {
          id: child,
          tId: duplicateId,
          tIndex: index,
          apply: false,
        });
      }
    }
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
